import { Injectable } from '@angular/core';
import { ImageDto } from '@picflow/types';
import { UploadObj } from '../classes/upload-obj.class';
import { DuplicateImagesDialogComponent } from '../gallery/dialogs/duplicate-images-dialog/duplicate-images-dialog.component';
import { DuplicateImagesInUploadsDialogComponent } from '../gallery/dialogs/duplicate-images-in-uploads-dialog/duplicate-images-in-uploads-dialog.component';
import { removeExtension } from '../lib/remove-extension';
import { DialogService } from './dialog.service';
import { ImageService } from './image.service';
import { NetworkService } from './network.service';

@Injectable({
  providedIn: 'root',
})
export class DuplicateImageService {
  constructor(
    private dialog: DialogService,
    private networkService: NetworkService,
    private imageService: ImageService
  ) {}

  public async handleDuplicates(
    names: string[],
    galleryId: string,
    uploads: UploadObj[]
  ): Promise<{ names: string[]; imagesToReplace: ImageDto[] }> {
    const duplicates = await this.findDuplicates(names, galleryId);

    let imagesToReplace: ImageDto[] = [];

    if (duplicates.length) {
      const action = await this.getDuplicateAction(duplicates);
      if (action === 'cancel') return { names: [], imagesToReplace: [] };
      if (action === 'override') {
        imagesToReplace = await this.getImagesFromNames(duplicates, galleryId);
      } else {
        names = names.filter(name => !duplicates.includes(name));
      }
    }

    const duplicatesInCurrentUploads = this.findDuplicatesInCurrentUploads(names, galleryId, uploads);
    if (duplicatesInCurrentUploads.length) {
      names = names.filter(name => !duplicatesInCurrentUploads.includes(name));
      this.dialog.open(DuplicateImagesInUploadsDialogComponent, { duplicates: duplicatesInCurrentUploads });
    }
    return { names, imagesToReplace };
  }

  public async handleDuplicateFiles(
    files: File[],
    galleryId: string,
    uploads: UploadObj[]
  ): Promise<{ files: File[]; imagesToReplace: ImageDto[] }> {
    const resp = await this.handleDuplicates(
      files.map(file => file.name),
      galleryId,
      uploads
    );
    return {
      files: resp.names.map(name => {
        const nameWithoutExtension = removeExtension(name);
        return files.find(f => removeExtension(f.name) === nameWithoutExtension);
      }),
      imagesToReplace: resp.imagesToReplace,
    };
  }

  public async getImagesFromNames(names: string[], galleryId: string): Promise<ImageDto[]> {
    const allGalleryImages = await this.getAllImages(galleryId);
    return names.map(name => {
      const nameWithoutExtension = removeExtension(name);
      return allGalleryImages.find(img => removeExtension(img.version_data.image_name) === nameWithoutExtension);
    });
  }

  public async findDuplicates(names: string[], galleryId: string): Promise<string[]> {
    const allGalleryImages = await this.getAllImages(galleryId);
    return names.filter(name => {
      const nameWithoutExtension = removeExtension(name);
      return allGalleryImages.find(img => removeExtension(img.version_data.image_name) === nameWithoutExtension);
    });
  }

  public findDuplicatesInCurrentUploads(names: string[], galleryId: string, uploads: UploadObj[]): string[] {
    const namesWithoutExtensions = names.map(removeExtension);
    return names.filter((name, i) => {
      const nameWithoutExtension = removeExtension(name);
      if (namesWithoutExtensions.indexOf(nameWithoutExtension) !== i) return true;
      return uploads.find(upload => {
        if (upload.galleryId !== galleryId) return false;
        if (upload.status === 'error') return false;
        return removeExtension(upload.file.name) === nameWithoutExtension;
      });
    });
  }

  private async getDuplicateAction(duplicates: string[]) {
    const resp = <'skip' | 'override'>await this.dialog.open(DuplicateImagesDialogComponent, { duplicates });
    return resp || 'cancel';
  }

  private async getAllImages(galleryId: string, isRetry = false): Promise<ImageDto[]> {
    if (isRetry) await this.networkService.onNetworkOnline();
    let allGalleryImages: ImageDto[];
    try {
      allGalleryImages = await this.imageService.getImagesForGallery(galleryId);
    } catch (err) {
      if (isRetry) throw err;
      return this.getAllImages(galleryId, true);
    }
    return allGalleryImages;
  }
}
